import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import {
  Text,
  TextInput,
  Select,
  Checkbox,
  Chip,
  Switch,
  Textarea,
} from "@mantine/core";
import moment from "moment";
import { useTranslation } from "react-i18next";
import ActionCellRender from "../../components/actionCellRender";
import { IconTrash, IconPencil, IconSettings } from "@tabler/icons";
import UserRoleList from "../../components/userRoleList";
import { useCellRender } from "../../hooks/useCellRender";
import { McaApprovalListBadge } from "../../components/mca/McaEntityIndicators";
import RemoteSelect2 from "../../components/remoteSelect2";
import RemoteSelect from "../../components/remoteSelect";
import { DatePicker } from "@mantine/dates";
import AddressList from "../../components/addressList";
import LicenceCheckList from "../../components/licenceCheckList";
import SfcLicenceCheckList from "../../components/sfcLicenceCheckList";
import ImagePicker from "../../components/imagePicker";
import FileList from "../../components/fileList";
import ConsultantCertificateList from "../../components/consultantCertificateList";
import UserActionList from "../../components/userActionList";
import ConsultantAllowanceList from "../../components/consultantAllowanceList";
import ConsultantOverridingPlans from "../../components/consultantOverridingPlans";
import Chips from "../../components/chips";
import DummyWidget from "../../components/dummyWidget";
import FormConfirmation from "../../components/formConfirmation";
import UniqueTextInput from "../../components/uniqueTextInput";
import PhoneNumberList from "../../components/phoneNumberList";
import EmailList from "../../components/emailList";
import ContactList from "../../components/contactList";
import { DEFAULT_PHONE } from "../../helpers/phoneUtils";
import UserClientList from "../../components/userClientList";
import UserCPDDetail from "../../components/userCPDDetail";
import _ from "lodash";
import ProviderLoginInfoRow from "../../components/providerLoginInfoRow.js";
import PersonalTradingAccountRow from "../../components/personalTradingAccountRow.js";
import OrderList from "../../components/orderList.js";
import {
  DATA_GENDERS,
  DATA_MARIAGE_STATUS,
  DATA_EDUCATION_LEVEL,
  DATA_COUNTRY,
  DATA_PAYMENT_METHOD,
  DATA_CONSULTANT_PAYMENT_METHOD,
  DATA_BANK,
  DATA_USER_TYPES,
  DATA_USER_CATEGORY,
  DATA_BANK_WITH_CODE,
  DATA_ID_TYPE,
} from "../../data/options";
import { LICENCE_ROLE_OPTIONS, SFC_LICENCE_ROLE_OPTIONS } from "../../data/licenceRoles";
import ReactJson from "react-json-view";
import UserOrderHistory from "../../components/userOrderHistory";
import Consultant from "./consultant";
import AutoCodeInput from "../../components/autoCodeInput";
import ActivityLog from "../../components/activityLog";
import UserBasicInfo from "../../components/userBasicInfo";
import UserSettingInfo from "../../components/userSettingInfo";
import UserEmploymentInfo from "../../components/userEmploymentInfo";
import { getUserDetailFormSchema } from "./userDetailFormSchema";
import { useExtraFieldsFormSchema } from "../../hooks/useExtraFieldsFormSchema";
// import { useTranslation } from "react-i18next";

const userImportTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/user/downloadTemplate`;
const userImportExistingTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/user/downloadExistingTemplate`;
const userPortalLoginImportTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/user/downloadPortalLoginTemplate`;
const userPortalLoginImportExistingTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/user/downloadPortalLoginExistingTemplate`;

const apiEntity = "user";
const initialValues = {
  address: "",
  isConsultant: true,
  files: [],
  userCategory: "INDIVIDUAL",
  userType: "CONSULTANT",
  idType: "HKID",

  consultantInfo: {
    licences: {
      l1: false,
      l2: false,
      l3: false,
      l4: false,
      l5: false,
    },
    sfcLicences: { type1: false, type4: false, type9: false },
    iaRole: "TR",
    mpfaRole: "SI",
    sfcRole: "LR",
    isChannel: false,
    certificate: [],
    paymentMethods: {
      type: "",
      accNumber: "",
      name: "",
      bank: "",
    },
    personalTradingAccounts: [],
  },
  phone: { ...DEFAULT_PHONE },
  phone2: { ...DEFAULT_PHONE },
  phones: [],
  emails: [],
  addresses: [],
};

// Export a function that returns the addSchema
export const getAddFormSchema = (t) => {
  const schema = {
    title: "New User",
    display: {
      mode: ENUM_FORM_DISPLAY.MODAL,
      size: "1000px", //xs, sm, xl  ....100%
    },
    showSaveBar: false,
    apiEntity,
    initialValues,
    validate: {
      name: (value) =>
        value?.length < 4 ? "Name must have at least 4 letters" : null,
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
      hkid: (value, formValues) => {
        const regrex = /^[A-Z]{1,2}[0-9]{6}\([0-9A]\)$/;
        if (formValues.idType === "HKID") {
          if (!regrex.test(value)) return t("It is not a valid HKID");
        }
        return null;
      },
      userRole: (value) => (!value ? "User Role must assign to user" : null),
    },
    layout: {
      containers: [
        {
          key: "t1",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.WIZARD,
          props: {
            variant: "default", //default | pills | outline
            defaultValue: "Category",
            orientation: "vertical",
            // style: { minHeight: "400px" },
          },
          steps: [
            {
              key: "t1",
              label: "Category",
            },
            {
              key: "t1-0",
              label: "Identity",
            },
            {
              key: "t1-1",
              label: "Basic Info",
            },
            {
              key: "t1-2",
              label: "Contact Info",
            },
            {
              key: "t1-3",
              label: "Employment",
            },
            // { key: "t1-4", label: "Licence" },
            { key: "t1-5", label: "Confirm" },
          ],
        },
        {
          key: "section-type",
          parent: "t1",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "User Category",
            titleOrder: 5,
            description: "",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },
        {
          key: "section-ID",
          parent: "t1-0",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "User Identity",
            titleOrder: 5,
            description: "Please enter the HKID of staff",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },

        {
          key: "section-info",
          parent: "t1-1",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          visibleOnly: (formValues) => {
            return formValues.userCategory == "INDIVIDUAL";
          },
          props: {
            title: "User Basic Information",
            description: "Please fill in the basic information of user",
            withBorder: false,
            ml: 20,
            mt: 0,
          },
        },
        {
          key: "section-basicInfo-corporate",
          parent: "t1-1",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          visibleOnly: (formValues) => {
            return formValues.userCategory == "CORPORATE";
          },
          props: {
            title: "Basic Info",
            titleOrder: 5,
            description: "Please enter basic info of client",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },

        {
          key: "employment-g",
          parent: "section-employment",
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          cols: [
            {
              key: "employment-g1",
              props: {
                span: 3,
              },
            },
            {
              key: "employment-g2",
              props: {
                span: 9,
              },
            },
          ],
        },
        {
          key: "basicInfo-g",
          parent: "section-info",
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          cols: [
            {
              key: "basicInfo-g1",
              props: {
                span: 6,
              },
            },
            {
              key: "basicInfo-g2",
              props: {
                span: 6,
              },
            },
          ],
        },
        {
          key: "section-contact-info",
          parent: "t1-2",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            withBorder: false,
            ml: 20,
            mt: 0,
            mb: 20,
          },
        },
        {
          key: "tab-contact-info",
          parent: "section-contact-info",
          type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
          props: {
            variant: "outline",
            size: "sm",
            defaultValue: "Phone Numbers",
          },
          tab: [
            {
              key: "tab-phones",
              label: "Phone Numbers",
            },
            {
              key: "tab-emails",
              label: "Emails",
            },
            {
              key: "tab-addresses",
              label: "Address",
            },
            {
              key: "tab-related-persons",
              label: "Related Person Info",
            },
          ],
        },

        {
          key: "section-employment",
          parent: "t1-3",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Employment Info",
            description: "Please fill in the basic employment info",
            withBorder: false,
            // ml: 10,
            mt: 0,
            // p: 20,
          },
        },
        // {
        // 	key: "section-licences",
        // 	parent: "t1-4",
        // 	type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        // 	props: {
        // 		title: "Licences",
        // 		description: "Please fill the licences of consultant",
        // 		withBorder: false,
        // 		ml: 10,
        // 		mt: 0,
        // 	},
        // },
        {
          key: "section-confirm",
          parent: "t1-5",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Confirmation",
            titleOrder: 3,
            description:
              "Please confirm to add user or you can go back to revise.",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },
      ],

      fields: [
        {
          name: "userCategory",
          component: Chips,
          parent: "section-type",
          props: {
            required: true,
            labelOrder: 5,
            description: "Please Select the category of user.",
            items: DATA_USER_CATEGORY,
            defaultValue: "INDIVIDUAL",
            multiple: false,
          },
        },

        {
          name: "idType",
          component: Select,
          parent: "section-ID",
          props: {
            required: true,
            label: "ID Type",
            placeholder: "",
            data: DATA_ID_TYPE,
            defaultValue: "ID No.",
            searchable: true,
          },
        },
        {
          name: "hkid",
          component: UniqueTextInput,
          parent: "section-ID",
          props: {
            required: true,
            uniqueEntity: "user",
            errorMessage: "User is already exist.",
            label: "ID No.",
            placeholder: "HKID / PASSPORT / BR",
            transform: "",
          },
        },

        {
          name: "idIssueCountry",
          component: Select,

          parent: "section-ID",
          props: {
            required: true,
            label: "ID Issue Country",
            placeholder: "",
            data: DATA_COUNTRY,
            defaultValue: "Hong Kong",
            searchable: true,
          },
        },

        {
          name: "name",
          component: TextInput,
          parent: "basicInfo-g1",
          props: {
            required: true,
            placeholder: "Chan Tai Man",
            label: "English Name",
          },
        },
        {
          name: "name",
          component: TextInput,
          parent: "section-basicInfo-corporate",

          props: {
            required: true,
            placeholder: "Chan Tai Man",
            label: "English Name",
          },
        },
        {
          name: "cname",
          component: TextInput,
          parent: "basicInfo-g1",
          props: {
            placeholder: "陳大文",
            label: "Chinese Name",
          },
        },
        {
          name: "cname",
          component: TextInput,
          parent: "section-basicInfo-corporate",

          props: {
            placeholder: "陳大文",
            label: "Chinese Name",
          },
        },

        {
          name: "aliasName",
          component: TextInput,
          parent: "basicInfo-g1",
          props: {
            placeholder: "Kenny",
            label: "Alias Name",
          },
        },
        {
          name: "aliasName",
          component: TextInput,
          parent: "section-basicInfo-corporate",

          props: {
            placeholder: "Kenny",
            label: "Alias Name",
          },
        },

        // {
        // 	name: "hkid",
        // 	component: TextInput,
        // 	parent: "basicInfo-g1",
        // 	props: {
        // 		required: true,
        // 		label: "HKID",
        // 		placeholder: "HKID / PASSPORT / BR",
        // 		transform: "",
        // 	},
        // },

        {
          name: "email",
          component: TextInput,
          parent: "basicInfo-g2",
          props: {
            required: true,
            placeholder: "taiman@gmail.com",
            label: "Login Email",
          },
        },
        {
          name: "email",
          component: TextInput,
          parent: "section-basicInfo-corporate",
          props: {
            required: true,
            placeholder: "taiman@gmail.com",
            label: "Login Email",
          },
        },
        {
          name: "phones",
          component: PhoneNumberList,
          parent: "tab-phones",
          props: {},
        },
        {
          name: "emails",
          component: EmailList,
          parent: "tab-emails",
          props: {},
        },
        {
          name: "addresses",
          component: AddressList,
          parent: "tab-addresses",
          props: {},
        },
        {
          name: "contacts",
          component: ContactList,
          parent: "tab-related-persons",
          props: {
            phoneField: "tel",
          },
        },

        {
          name: "birthday",
          component: DatePicker,
          parent: "basicInfo-g1",
          type: "datePicker",
          props: {
            required: true,
            label: "Date of Birth",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },

        {
          name: "userRole",
          component: RemoteSelect2,
          parent: "basicInfo-g2",
          props: {
            label: "User Role",
            apiEntity: "userRole",
            required: true,
            valueField: "_id",
            labelField: "name",
            searchFields: ["name"],
            sort: {
              by: "name",
              order: "asc",
            },
          },
        },
        {
          name: "userRole",
          component: RemoteSelect2,
          parent: "section-basicInfo-corporate",
          props: {
            label: "User Role",
            apiEntity: "userRole",
            required: true,
            valueField: "_id",
            labelField: "name",
            searchFields: ["name"],
            sort: {
              by: "name",
              order: "asc",
            },
          },
        },
        {
          name: "educationLevel",
          component: Select,
          parent: "basicInfo-g2",
          props: {
            label: "Education Level",
            placeholder: "",
            data: DATA_EDUCATION_LEVEL,
            defaultValue: "UG",
            searchable: true,
          },
        },

        {
          name: "gender",
          component: Select,
          parent: "basicInfo-g2",
          props: {
            label: "Gender",
            placeholder: "",
            data: DATA_GENDERS,
            defaultValue: "",
            searchable: true,
          },
        },
        {
          name: "mariage",
          component: Select,
          parent: "basicInfo-g1",
          props: {
            // required: true,
            label: "Mariage Status",
            placeholder: "",
            data: DATA_MARIAGE_STATUS,
            defaultValue: "Single",
            searchable: true,
          },
        },
        //refered By
        {
          name: "referedBy",
          component: RemoteSelect2,
          parent: "basicInfo-g2",
          props: {
            label: "Refered By",
            apiEntity: "user",
            valueField: "_id",
            labelField: "name",
            allowClear: true,
            labelRender: (data) =>
              ` [${data.staffId}] ${data.name} ${data.aliasName}`,
            pageSize: 100,
            size: "sm",
            searchFields: [
              "name",
              "cname",
              "staffId",
              "email",
              "phone",
              "aliasName",
            ],
            clearable: true,
            sort: {
              by: "staffId",
              order: "asc",
            },
          },
        },

        {
          name: "image",
          component: ImagePicker,
          parent: "employment-g1",
          props: {},
        },

        {
          name: "staffId",
          component: AutoCodeInput,
          parent: "employment-g2",
          props: {
            required: true,
            uniqueEntity: "user",
            errorMessage: "Staff ID / User Code is already exist.",
            label: "Staff ID / Code",
            placeholder: "AA001",
          },
        },

        {
          name: "userType",
          component: Select,
          parent: "employment-g2",
          props: {
            // required: true,
            label: "User Type",
            placeholder: "Consultant / Channel / Back office",
            data: DATA_USER_TYPES,
            defaultValue: "Consultant",
            required: true,
            searchable: true,
          },
        },
        {
          name: "consultantInfo.overridingPlan",
          component: RemoteSelect2,
          parent: "employment-g2",
          props: {
            label: "Overriding Plan",
            apiEntity: "commissionOverridingPlan",
            valueField: "_id",
            labelField: "name",
            searchFields: ["name"],
            required: true,
            sort: {
              by: "name",
              order: "asc",
            },
          },
        },

        {
          name: "workTitle",
          component: TextInput,
          parent: "employment-g2",
          props: {
            label: "Work Title",
          },
        },
        {
          name: "employmentDate",
          component: DatePicker,
          parent: "employment-g2",
          type: "datePicker",

          props: {
            label: "Join Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "teams",
          component: RemoteSelect2,
          parent: "employment-g2",
          props: {
            label: "Teams",
            apiEntity: "team",
            valueField: "_id",
            labelField: "name",
            searchFields: ["name"],
            sort: {
              by: "name",
              order: "asc",
            },
            multiple: true,
          },
        },
        {
          name: "accessCardNumber",
          component: TextInput,
          parent: "employment-g2",
          props: {
            label: "Access Card Number",
          },
        },
        {
          name: "octopusNumber",
          component: TextInput,
          parent: "employment-g2",
          props: {
            label: "Octopus Number",
          },
        },
        {
          name: "qualifications",
          component: Textarea,
          parent: "employment-g2",
          props: {
            label: "Qualifications",
            minRows: 2,
            autosize: true,
          },
        },

        // {
        // 	name: "isConsultant",
        // 	component: Switch,
        // 	parent: "employment-g1",
        // 	props: {
        // 		type: "checkbox",
        // 		label: "Is Consultant",
        // 		mt: 20,
        // 	},
        // },

        // {
        // 	name: "consultantInfo.isChannel",
        // 	component: Switch,
        // 	parent: "employment-g1",
        // 	props: {
        // 		type: "checkbox",
        // 		label: "Is Channel",
        // 		mt: 20,
        // 	},
        // },

        // {
        // 	name: "consultantInfo.licences",
        // 	component: LicenceCheckList,
        // 	parent: "section-licences",
        // 	props: {
        // 		label: "Licences",
        // 	},
        // },

        {
          name: "confirm",
          component: FormConfirmation,
          parent: "section-confirm",
          props: {},
        },
      ],
    },
  };
  return schema;
};

const User = ({ role }) => {
  const [cellRender] = useCellRender();
  const { t } = useTranslation();
  const location = useLocation();

  const tableSchema = {
    showActionColumn: true,
    select: "name staffId userType userRole email status mcaRequestId mcaPending",

    columns: [
      {
        field: "name",
        headerName: "Name",
        sortable: true,
        width: 300,
        cellRender: (column, data) => (
          <>
            <McaApprovalListBadge entity={data} />
            {cellRender.User(data, false)}
          </>
        ),
      },
      {
        field: "staffId",
        headerName: "Staff ID",
        sortable: true,
        width: 120,
      },
      {
        field: "_filterUserCategory",
        headerName: "User Category",
        filterOnly: true,
        filterable: true,
        filter: {
          apiEntity: "user",
          field: "userCategory",
          label: "User Category",
          width: 250,
          height: 200,
        },
      },
      {
        field: "userType",
        headerName: "User Type",
        sortable: true,
        filterable: true,
        filter: {
          apiEntity: "user",
          field: "userType",
          label: "Types",

          width: 300,
          height: 200,
        },
      },
      {
        field: "userRole",
        headerName: "User Role",
        sortable: true,
        cellRender: (column, data) => data.userRole?.name,
        filterable: true,
        filter: {
          apiEntity: "user",
          field: "userRole",
          label: "User Role",
          populate: {
            model: "UserRole",
            select: "name cname code",
            labelField: "name",
          },
          width: 300,
          height: 200,
        },
      },
      {
        field: "email",
        headerName: "Email",
        sortable: true,
      },

      // {
      //   field: "employmentDate",
      //   headerName: "Join Date",
      //   sortable: true,
      //   cellRender: (column, data) =>
      //     data.employmentDate
      //       ? moment(data.employmentDate).format("YYYY-MM-DD")
      //       : "--",
      // },

      {
        field: "status",
        headerName: "Status",
        sortable: true,
        cellRender: (column, data) => cellRender.Status(data.status),
        filterable: true,
        filter: {
          apiEntity: "user",
          field: "status",
          label: "Status",

          width: 300,
          height: 200,
        },
      },

      {
        field: "action",
        cellRender: ActionCellRender,
        isAction: true,
        actions: [
          {
            name: "edit",
            icon: IconPencil,
            props: {
              //   color:"red"
            },
          },
        ],
        actionMenu: [
          // {
          // 	name: "delete",
          // 	label: "Delete",
          // 	icon: IconTrash,
          // 	props: {
          // 		color: "red",
          // 	},
          // },
        ],
      },
    ],

    searchableFields: [
      "code",
      "name",
      "userType",
      "cname",
      "email",
      "phone",
      "staffId",
      "status",
      "aliasName",
    ],
  };

  const addFormSchema = getAddFormSchema(t);
  const baseEditFormSchema = useMemo(
    () => getUserDetailFormSchema(t, role),
    [t, role]
  );
  const editFormSchema = useExtraFieldsFormSchema(
    baseEditFormSchema,
    "User",
    {},
    [t, role]
  );

  const formSchema = {
    add: addFormSchema,
    edit: editFormSchema,
  };

  return (
    <>
      <PageList
        apiEntity={"user"}
        tableSchema={tableSchema}
        formSchema={formSchema}
        showImport={true}
        showTemplateDownload={true}
        importVariants={[
          {
            key: "userData",
            label: "User Data",
            templateDownloads: [
              {
                url: userImportTemplateUrl,
                text: "Download Blank Template",
              },
              {
                url: userImportExistingTemplateUrl,
                text: "Download Existing Users",
              },
            ],
          },
          {
            key: "portalLogins",
            label: "Portal Logins",
            templateDownloads: [
              {
                url: userPortalLoginImportTemplateUrl,
                text: "Download Blank Template",
              },
              {
                url: userPortalLoginImportExistingTemplateUrl,
                text: "Download Existing Logins",
              },
            ],
            importModes: [
              { value: "insert", label: "Insert (append)" },
              {
                value: "replace",
                label: "Replace (overwrite per user)",
              },
            ],
            defaultImportMode: "insert",
            onImport: (file, { importMode }, api) =>
              api.User.importPortalLoginBatch(file, { importMode }),
          },
        ]}
        initSort={{ by: "staffId", order: "asc" }}
        header={{
          component: UserRoleList,
          props: {
            title: t("User Management"),
            returnPath: location.pathname,
          },
        }}
      />
    </>
  );
};

export default User;
